import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { AlertCircle, ArrowLeft, MailCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.forgotPassword(email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #D1FAE5 0%, #FEF3C7 100%)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="p-8 rounded-3xl shadow-lg border-2 border-slate-200">
          <div className="text-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-24 h-24 mx-auto mb-3 object-contain"
              data-testid="forgot-mascot-image"
            />
            <h1 className="text-3xl font-heading font-bold text-emerald-600 tracking-tight" data-testid="forgot-title">
              Forgot Password?
            </h1>
            <p className="text-slate-600 mt-2" data-testid="forgot-subtitle">
              {sent
                ? "Check your email for the reset link"
                : "Enter your email and we'll send you a reset link"}
            </p>
          </div>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-3 flex items-start gap-2" data-testid="forgot-error">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-slate-700 font-semibold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 rounded-2xl border-2 focus:border-emerald-500 focus:ring-emerald-200"
                  placeholder="your@email.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                data-testid="forgot-submit-button"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="forgot-success">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring' }}
                className="bg-emerald-100 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto"
              >
                <MailCheck className="w-12 h-12 text-emerald-600" strokeWidth={2.5} />
              </motion.div>
              <p className="text-center text-slate-600" data-testid="forgot-success-message">
                If an account with <span className="font-bold">{email}</span> exists, we've sent a password reset link. Please check your inbox (and spam folder). The link is valid for <span className="font-bold">1 hour</span>.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
                data-testid="forgot-back-to-login"
              >
                Back to Login
              </Button>
            </div>
          )}

          {!sent && (
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-emerald-600 font-bold hover:text-emerald-700 inline-flex items-center gap-1"
                data-testid="forgot-back-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
